import React, {useRef} from 'react';

import {Animation} from '@pb/kit-component-animation';
import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from '../../dialog.module.styl';

import type {AnimationDirection} from '../../hooks/useDialogStepper';
import type {ComponentPropsWithoutRef, ReactNode} from 'react';

const classNames = withCssModule(styles);

const ANIMATION_DURATION = 300;

export type DialogStepperContentProps = {
    /**
     * The current step content to display
     */
    children: ReactNode,
    /**
     * Animation direction from useDialogStepper hook
     */
    animationDirection: AnimationDirection,
    /**
     * Unique key for the current step (usually the step index)
     * This triggers React to remount the component for animations
     */
    stepKey: string | number,
} & Omit<ComponentPropsWithoutRef<'div'>, 'children'>;

/**
 * DialogStepperContent component handles animations for multi-step dialogs.
 * Use this with the useDialogStepper hook to create smooth step transitions.
 */
export const DialogStepperContent = ({
    children,
    animationDirection,
    stepKey,
    className,
    ...restProps
}: DialogStepperContentProps) => {
    const nodeRef = useRef<HTMLDivElement>(null);

    return (
        <div
            {...restProps}
            className={classNames(
                'dialog-step-wrapper',
                className,
            )}>
            <Animation
                exit={false}
                component="span"
                timeout={ANIMATION_DURATION}
                nodeRef={nodeRef}
                classNames={{
                    enter: styles[`dialog-step_animate-${animationDirection}-enter`],
                    enterActive: styles[`dialog-step_animate-${animationDirection}-enter-active`],
                }}>
                <div ref={nodeRef} key={stepKey} className={styles['dialog-step']}>
                    {children}
                </div>
            </Animation>
        </div>
    );
};

DialogStepperContent.displayName = 'DialogStepperContent';

